import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SettingsIcon from '@mui/icons-material/Settings';
import { ModernCard, ModernCheckbox } from './ModernComponents';
import './styles/step_constraints.style.scss';

const StepConstraints = ({
  selectedConstraints,
  constraintValues,
  toggleConstraintSelection,
  updateConstraintValue,
  coProducts = [],
  totalSurface = 0,
}) => {
  const { t } = useTranslation();

  const constraintsArray = useMemo(() => {
    const CONSTRAINT_LABELS = {
      irrigation: t('simulation.step.constraints.labels.irrigationVolume'),
      irrigableParcelNumber: t('simulation.step.constraints.labels.irrigableParcelNumber'),
      activateCAPCategory: t('simulation.step.constraints.labels.activateCAP'),
      winterCrop: t('simulation.constraints.winterCrop'),
      cerealePaille: t('simulation.constraints.cerealCulture'),
      TFIHerbicideConstraint: t('simulation.constraints.maxIftHerbicide'),
      TFIWithoutHerbicideConstraint: t('simulation.constraints.maxIftNonHerbicide'),
      nitrogen: t('common.nitrogen'),
      coverProposal: t('simulation.constraints.coverProposal'),
      extraBiomass: t('simulation.constraints.extraBiomass'),
      intercropSurface: t('simulation.step.constraints.labels.maxSurface'),
    };

    const CONSTRAINT_GROUPS = [
      {
        name: 'irrigationConstraint',
        displayName: t('common.irrigability'),
        constraints: [
          { name: 'irrigation', inputType: 'minmax' },
          { name: 'irrigableParcelNumber', inputType: 'minmax' },
        ],
      },
      {
        name: 'activateCAPCategory',
        displayName: t('simulation.step.constraints.categories.capEcoSchemes'),
        constraints: [
          {
            name: 'activateCAPCategory',
            inputType: 'min',
            inputLabel: t('simulation.step.constraints.labels.capPointsLabel'),
          },
        ],
      },
      {
        name: 'percentConstraints',
        displayName: t('simulation.step.constraints.categories.percentConstraints'),
        constraints: [
          { name: 'winterCrop', inputType: 'minmax' },
          { name: 'cerealePaille', inputType: 'minmax' },
        ],
      },
      {
        name: 'iftConstraints',
        displayName: t('common.ift'),
        constraints: [
          { name: 'TFIHerbicideConstraint', inputType: 'max' },
          { name: 'TFIWithoutHerbicideConstraint', inputType: 'max' },
        ],
      },
      {
        name: 'indicator',
        displayName: t('simulation.step.constraints.categories.indicators'),
        constraints: [
          { name: 'nitrogen', inputType: null },
          { name: 'coverProposal', inputType: null },
          { name: 'extraBiomass', inputType: null },
        ],
      },
      {
        name: 'intercrops',
        displayName: t('simulation.constraints.intermediate'),
        constraints: [{ name: 'intercropSurface', inputType: 'maxWithUnit' }],
      },
    ];

    const baseConstraints = CONSTRAINT_GROUPS.map((group) => ({
      name: group.name,
      displayName: group.displayName,
      constraints: group.constraints.map((item) => ({
        name: item.name,
        displayName: CONSTRAINT_LABELS[item.name],
        inputType: item.inputType,
        inputLabel: item.inputLabel || null,
        coProductFlag: false,
      })),
    }));

    if (coProducts.length > 0) {
      const coProductsCategory = {
        name: 'coProducts',
        displayName: t('simulation.step.constraints.categories.coProducts'),
        constraints: coProducts.map((coProduct) => ({
          name: `${coProduct.name}`,
          displayName: coProduct.displayNameSimulation,
          inputType: 'minmax',
          coProductFlag: true,
        })),
      };
      return [coProductsCategory, ...baseConstraints];
    }

    return baseConstraints;
  }, [coProducts, t]);

  const isConstraintSelected = (name) => selectedConstraints.includes(name);

  return (
    <div className="stepConstraints">
      <ModernCard title={t('simulation.step.constraints.title')} icon={SettingsIcon}>
        <div className="constraintsList">
          {constraintsArray.map((constraintsItem) => (
            <div key={constraintsItem.name} className="constraintGroup">
              {constraintsItem.constraints ? (
                <div className="constraintCategory">
                  <p className="constraintCategoryTitle">{constraintsItem.displayName}</p>
                  <div className="constraintItems">
                    {constraintsItem.constraints.map((constraint) => (
                      <ModernCheckbox
                        key={constraint.name}
                        label={constraint.displayName}
                        checked={isConstraintSelected(constraint.name)}
                        inputType={constraint.inputType}
                        inputLabel={constraint.inputLabel}
                        constraintName={constraint.name}
                        constraintValues={constraintValues[constraint.name]}
                        onUpdateConstraintValue={updateConstraintValue}
                        totalSurface={totalSurface}
                        onChange={() =>
                          toggleConstraintSelection(constraint.name, {
                            ...(constraint.inputType && { hasInput: true }),
                            ...(constraint.coProductFlag && { coProductFlag: true }),
                          })
                        }
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <ModernCheckbox
                  key={constraintsItem.name}
                  label={constraintsItem.displayName}
                  checked={isConstraintSelected(constraintsItem.name)}
                  inputType={constraintsItem.inputType}
                  constraintName={constraintsItem.name}
                  constraintValues={constraintValues[constraintsItem.name]}
                  onUpdateConstraintValue={updateConstraintValue}
                  totalSurface={totalSurface}
                  onChange={() => toggleConstraintSelection(constraintsItem.name)}
                />
              )}
            </div>
          ))}
        </div>
      </ModernCard>
    </div>
  );
};

export default StepConstraints;
